import { useMemo } from 'react';
import { useUserRoles } from './useUserRoles';
import { useAuthQuery } from './useAuthQuery';
import { useAuth } from './useAuth';
import type { AdminCheckResult } from '@/types/auth';

// Simplified admin check using standardized auth patterns
export const useAdminCheck = (): AdminCheckResult => {
  const { data: userRoles, isLoading: isRolesLoading, isError: isRolesError } = useUserRoles();
  const { user } = useAuth();

  // Primary admin check from user roles
  const isAdminFromRoles = userRoles?.some(role => role.role_code === 'ROLE_ADMIN') || false;

  // Fallback admin check only when needed
  const fallbackAdminQuery = useAuthQuery<boolean>({
    queryKey: ['admin-fallback-check', user?.id],
    queryFn: async () => {
      const { checkUserIsAdmin } = await import('@/lib/queries/authQueries');
      return checkUserIsAdmin(user?.id || '');
    },
    requiresAuth: true,
    requiresSession: true,
    enabled: !!user?.id && (isRolesError || (!isRolesLoading && !isAdminFromRoles)),
    staleTime: 10 * 60 * 1000, // 10 minutes - cache admin status longer
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Memoize results to prevent unnecessary re-renders
  return useMemo(() => ({
    isAdmin: isAdminFromRoles || fallbackAdminQuery.data || false,
    isLoading: isRolesLoading || (fallbackAdminQuery.isLoading && !isAdminFromRoles),
    hasRoles: (userRoles?.length || 0) > 0,
    isRolesError
  }), [isAdminFromRoles, fallbackAdminQuery.data, fallbackAdminQuery.isLoading, isRolesLoading, userRoles?.length, isRolesError]);
};